import { useState } from "react";

import dayjs from "dayjs";

import {
  Box,
  Grid,
  Typography,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Switch,
  useTheme
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

import { formatDecimal } from "@aclymatepackages/formatters";

import Link from "../../../../atoms/mui/Link";

import {
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useMutateCachedCollection
} from "../../../../../helpers/firebase";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";
import { surveyDataStatusMap } from "../../../../../helpers/components/vendors";

const buildVendorAvailableYears = (startDate) => {
  const startYear = dayjs(startDate).year();
  const currentYear = dayjs().year();
  return Array.from(
    { length: currentYear - startYear },
    (_, i) => currentYear - 1 - i
  );
};

const SurveyDataStatusChip = ({ status }) => {
  const config = surveyDataStatusMap[status];
  if (!config) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={config.label}
      color={config.chipColor}
      variant="outlined"
    />
  );
};

const SHARED_SURVEY_STATUSES = new Set(["pending", "accepted"]);

const YearShareRow = ({
  year,
  entry,
  isLocked,
  hasRevenue,
  isLoading,
  onToggle
}) => {
  const { palette } = useTheme();
  const isShared = SHARED_SURVEY_STATUSES.has(entry?.status);

  const toggle = (
    <Switch
      checked={isShared}
      disabled={!hasRevenue || isLoading}
      onChange={() => onToggle(year, isShared)}
      color="primary"
    />
  );

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{year}</Typography>
      </TableCell>
      <TableCell>
        {entry?.status ? (
          <SurveyDataStatusChip status={entry.status} />
        ) : (
          <Typography variant="caption" color="textSecondary">
            Not shared
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          wrap="nowrap"
        >
          {entry?.tonsCo2ePerDollar && (
            <Grid item>
              <Typography variant="caption" color="textSecondary">
                {formatDecimal(entry.tonsCo2ePerDollar)} tCO₂e/$
              </Typography>
            </Grid>
          )}
          {!isLocked && (
            <Grid item>
              <Tooltip title="This year is not yet closed — data may still change">
                <LockIcon
                  style={{ fontSize: 16, color: palette.text.secondary }}
                />
              </Tooltip>
            </Grid>
          )}
          <Grid item>
            {hasRevenue ? (
              toggle
            ) : (
              <Tooltip title="Set your annual revenue in Settings → Company to share emissions data">
                <span>{toggle}</span>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

const PaidDataSharingTab = ({
  relationshipId,
  surveyData = [],
  buyerBomRequests = [],
  onBuyerBomRequestUpdated
}) => {
  const { palette } = useTheme();
  const [{ startDate, revenueYears = [] }] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const mutateCachedCollection = useMutateCachedCollection();

  const [loadingYear, setLoadingYear] = useState(null);
  const [loadingLifecycleId, setLoadingLifecycleId] = useState(null);
  const [shareError, setShareError] = useState(null);

  const lockedYears = new Set(
    (accountingYears || [])
      .map(({ date }) => dayjs(date?.toDate ? date.toDate() : date).year())
      .filter(Boolean)
  );

  const availableYears = startDate ? buildVendorAvailableYears(startDate) : [];

  const getYearEntry = (year) =>
    surveyData.find((e) => Number(e.year) === year) || null;

  const hasRevenueForYear = (year) => {
    const entry = (revenueYears || []).find((r) => Number(r.year) === year);
    return !!entry?.revenue;
  };

  const onToggleYear = async (year, currentlyShared) => {
    setLoadingYear(year);
    setShareError(null);
    const share = !currentlyShared;
    try {
      const result = await fetchOurApi({
        path: "/vendors/share-paid-account-survey-data",
        method: "POST",
        data: { relationshipId, year, share },
        callback: (res) => res
      });
      if (result?.error) {
        setShareError(
          result.message || "Failed to share data. Please try again."
        );
        return;
      }
      if (result?.surveyData) {
        // surveyData is written server-side by share-paid-account-survey-data
        // (business-logic-computed, not a plain client-owned field) --
        // revalidate the cache instead of writing the response back directly.
        await mutateCachedCollection("customers");
      }
    } catch (_) {
      setShareError("Failed to share data. Please try again.");
    } finally {
      setLoadingYear(null);
    }
  };

  const actionableBomProducts = buyerBomRequests.filter(
    ({ bomStatus }) => bomStatus === "requested" || bomStatus === "confirmed"
  );

  const onToggleBom = async (lifecycleEventId, currentlyConfirmed) => {
    setLoadingLifecycleId(lifecycleEventId);
    const share = !currentlyConfirmed;
    try {
      await fetchOurApi({
        path: "/vendors/share-paid-account-product-bom",
        method: "POST",
        data: { lifecycleEventId, share }
      });
      updateCollectionDoc("product-lifecycle-events", lifecycleEventId, {
        bomStatus: share ? "confirmed" : "requested",
        ...(share ? { dateConfirmed: new Date() } : {})
      });
      if (onBuyerBomRequestUpdated) {
        onBuyerBomRequestUpdated(
          lifecycleEventId,
          share ? "confirmed" : "requested"
        );
      }
    } finally {
      setLoadingLifecycleId(null);
    }
  };

  return (
    <Box>
      {shareError && (
        <Box mb={2}>
          <Alert severity="error" onClose={() => setShareError(null)}>
            {shareError}
          </Alert>
        </Box>
      )}
      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          Annual Emissions Intensity
        </Typography>
        {revenueYears.length === 0 && (
          <Box
            mb={2}
            p={2}
            bgcolor={palette.backgroundGray.light}
            borderRadius={1}
          >
            <Typography variant="body2" color="textSecondary">
              Set your annual revenue in{" "}
              <Link href="/platform/company/company">Settings → Company</Link>{" "}
              to share your emissions intensity with this customer.
            </Typography>
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Shared</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableYears.map((year) => (
              <YearShareRow
                key={year}
                year={year}
                entry={getYearEntry(year)}
                isLocked={lockedYears.has(year)}
                hasRevenue={hasRevenueForYear(year)}
                isLoading={loadingYear === year}
                onToggle={onToggleYear}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Product BOMs
        </Typography>
        {actionableBomProducts.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No BOM requests from this customer.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Shared</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actionableBomProducts.map(
                ({ lifecycleId, name, bomStatus, hasProductData }) => {
                  const isConfirmed = bomStatus === "confirmed";
                  const canShare = isConfirmed || hasProductData;
                  const toggle = (
                    <Switch
                      checked={isConfirmed}
                      disabled={loadingLifecycleId === lifecycleId || !canShare}
                      onChange={() => onToggleBom(lifecycleId, isConfirmed)}
                      color="primary"
                    />
                  );
                  return (
                    <TableRow key={lifecycleId}>
                      <TableCell>
                        <Typography variant="body2">{name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            isConfirmed ? "BOM Confirmed" : "BOM Requested"
                          }
                          size="small"
                          color={isConfirmed ? "secondary" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canShare ? (
                          toggle
                        ) : (
                          <Tooltip title="Fill in this product's components on the Products page before sharing.">
                            <span>{toggle}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default PaidDataSharingTab;
